import type { TargetDTO } from '@devrobot/shared';
import { Server } from 'lucide-react';
import { useProjectTargets, useProjects } from '../../../api/queries';
import { ErrorAlert } from '../../../components/ErrorAlert';
import { SkeletonLines } from '../../../components/Skeleton';
import { StatusBadge } from '../../../components/StatusBadge';
import type { StepProps } from './model';

export const isMigratableSource = (t: TargetDTO) => !t.removedAt && t.state.process !== 'not-deployed';

/** Database and volume names a target would copy by default (volumes that back a database are copied with it). */
export function defaultDataOf(t: TargetDTO): { databases: string[]; volumes: string[] } {
  const dbVolumes = new Set(t.databases.map((d) => d.volume));
  return { databases: t.databases.map((d) => d.service), volumes: t.volumes.filter((v) => v.declared && !dbVolumes.has(v.name)).map((v) => v.name) };
}

export function SourceStep({ state, dispatch }: StepProps) {
  const projects = useProjects();
  const targets = useProjectTargets(state.project, Boolean(state.project));
  const candidates = (state.project ? (targets.data ?? []) : []).filter(isMigratableSource);

  return (
    <div className="space-y-5">
      <fieldset className="fieldset max-w-md">
        <label className="fieldset-legend" htmlFor="migration-project">
          Project
        </label>
        {projects.isPending ? <div className="skeleton h-10 w-full" /> : null}
        <ErrorAlert error={projects.error} onRetry={() => void projects.refetch()} />
        {projects.data ? (
          <select id="migration-project" className="select w-full" value={state.project} onChange={(e) => dispatch({ type: 'set-project', project: e.target.value })}>
            <option value="">Choose a project…</option>
            {projects.data.map((p) => (
              <option key={p.key} value={p.key}>
                {p.name} ({p.key})
              </option>
            ))}
          </select>
        ) : null}
      </fieldset>

      {state.project ? (
        <fieldset>
          <legend className="mb-2 text-sm font-semibold">Deployment to migrate from</legend>
          {targets.isPending ? <SkeletonLines lines={3} /> : null}
          <ErrorAlert error={targets.error} onRetry={() => void targets.refetch()} />
          {targets.data && candidates.length === 0 ? <p className="text-sm text-base-content/60">This project has no running deployment. Deploy it first; only deployed targets can be migrated.</p> : null}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {candidates.map((t) => {
              const checked = state.sourceTargetId === t.id;
              return (
                <label
                  key={t.id}
                  className={`card dr-surface cursor-pointer border-2 p-4 transition-colors ${checked ? 'border-primary' : 'border-transparent'}`}
                >
                  <span className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="migration-source"
                      className="radio radio-primary radio-sm mt-1"
                      checked={checked}
                      aria-label={`${t.environment} on ${t.vps.name}`}
                      onChange={() => dispatch({ type: 'set-source', targetId: t.id, environment: t.environment, vpsId: t.vps.id, ...defaultDataOf(t) })}
                    />
                    <span className="min-w-0 flex-1 space-y-1.5">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">{t.environment}</span>
                        {t.environmentProtected ? <span className="badge badge-ghost badge-xs">protected</span> : null}
                        <StatusBadge kind="process" value={t.state.process} size="xs" stale={t.state.stale} />
                      </span>
                      <span className="flex items-center gap-1.5 text-sm text-base-content/70">
                        <Server className="size-3.5" aria-hidden="true" /> {t.vps.name}
                        <StatusBadge kind="connection" value={t.vps.connection} size="xs" />
                      </span>
                      <span className="block font-mono text-xs text-base-content/60">
                        {t.currentRelease ? `${t.currentRelease.branch}@${t.currentRelease.shortSha}` : 'no release recorded'}
                      </span>
                      <span className="block text-xs text-base-content/60">
                        {t.databases.length} database{t.databases.length === 1 ? '' : 's'} · {t.volumes.length} volume{t.volumes.length === 1 ? '' : 's'}
                        {t.activeJob ? ' · a job is running' : ''}
                      </span>
                      {!t.can.backup ? <span className="block text-xs dr-ink-warning">Your role cannot back up this target; the migration needs a fresh backup.</span> : null}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      ) : null}
    </div>
  );
}
